import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
This guide covers calling CloudQuote from a **server**, **scheduled job**, or any other environment where your code runs in a private location and you control its secrets.

The big advantage: you can use a **private application key**, which is the simplest credential type on the platform.

## What "back-end" means here

Back-end applications include:

- Web applications rendered server-side (PHP, Java, .NET, ASP, Ruby, Node, Python, Go).
- Cron jobs and scheduled scripts.
- ETL pipelines that pull market data into a warehouse.
- Webhook handlers that enrich payloads with CloudQuote data.
- Internal microservices.

What they share: code is never delivered to end users, and secrets stored on the server stay on the server.

## Two supported authentication paths

| Path | When to use it |
| :---- | :---- |
| **Private application key** | The default. Simplest setup. Works for almost everyone. |
| **Public key + whitelisted IP range** | Enterprise deployments where the same key is shared across many servers and you want IP-level enforcement. |

## Path 1: Private application key

The most common pattern. The key acts like a password — keep it in a secret manager (AWS Secrets Manager, HashiCorp Vault, environment variables, whatever your team uses) and never commit it to source control.

### Set it up

1. Sign in to the API Dashboard.
2. Open **My Account → API Credentials**.
3. Create a private application key. Copy it now — it's only shown once.
4. Store it in your secrets manager.

### Make a request

\`\`\`bash
curl -H 'Authorization: privateKey YOUR_KEY_HERE' \\
  'https://api.cloudquote.io/fcon/getQuote.json?symbol=AAPL'
\`\`\`

\`\`\`js
// Node.js
const response = await fetch(
  "https://api.cloudquote.io/fcon/getQuote.json?symbol=AAPL",
  {
    headers: { "Authorization": \`privateKey \${process.env.CLOUDQUOTE_KEY}\` }
  }
);
\`\`\`

\`\`\`python
# Python
import os, requests

response = requests.get(
    "https://api.cloudquote.io/fcon/getQuote.json",
    params={"symbol": "AAPL"},
    headers={"Authorization": f"privateKey {os.environ['CLOUDQUOTE_KEY']}"},
)
quote = response.json()
\`\`\`

### Rules CloudQuote enforces

- Request **must not** include a \`Referer:\` or \`Origin:\` header.
- Request **must not** include any CORS headers.
- These are the markers of a browser request — CloudQuote rejects them on private-key calls so a leaked key behaves no differently than any other rejected request.

## Path 2: Public key + whitelisted IP range

Used by larger organizations where a single key is provisioned to a fleet of servers and you want IP-level enforcement on top of the key check.

### Set it up

1. Create a public application key in the API Dashboard.
2. Add a whitelisted IP range (CIDR notation) for the servers that should be allowed to use it.

### Make a request

\`\`\`bash
curl -H 'Authorization: publicKey YOUR_PUBLIC_KEY' \\
  'https://api.cloudquote.io/fcon/getQuote.json?symbol=AAPL'
\`\`\`

CloudQuote rejects the request unless the source IP falls inside the registered range.

## Production guidance

### Secret hygiene

- Never check keys into git. Even private repos.
- Rotate keys on a schedule (every 90 days is a reasonable default).
- Issue **one key per service**, not one key for the whole company. Lets you rotate without coordinating across teams.

### Caching

Server-side caching is a force multiplier:

- **Quotes** can be cached for a few seconds without changing the user experience.
- **Historical bars** can be cached for hours or days — they don't change.
- **Fundamentals** can be cached for a day or longer.

Caching keeps you well below rate limits and keeps response times fast.

### Retry logic

Wrap calls in **exponential backoff** with a cap. The platform returns:

- \`429\` for rate limits → back off, then retry.
- \`500\` for transient errors → retry safely.
- \`401\`, \`403\`, \`404\` → don't retry. Fix the request.

A good default: 3 retries with 250 ms / 500 ms / 1 s backoff.

### Bulk requests

Wherever an endpoint accepts a comma-separated list (e.g., \`symbols=AAPL,MSFT,GOOGL\`), use it. One request for 50 tickers is much cheaper — for you and for the platform — than 50 requests for one ticker.

### Logging and observability

Log the response time and HTTP status of every CloudQuote call. The API Dashboard shows the platform's view of your usage; pairing it with your own logs makes debugging much faster when something looks off.

### Concurrency

The platform handles high concurrency, but your client should still bound it. A semaphore that caps in-flight calls (start with 50 concurrent) prevents your worker pool from being saturated by a slow downstream.

## Where to go next

- For browser/mobile/desktop integrations, see [Front-End Integration](/docs/frontend-integration).
- For the full credential reference, see [Authentication & API Keys](/docs/authentication).
- For higher-level conversational orchestration, see the [Chat API](/docs/chat-api).
`;

export default function BackendIntegrationPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "backend-integration")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
